/**
 * Asset ↔ project assignment history — the `/api/v1/asset-assignments` wire
 * contract (docs/MATERIALS.md).
 *
 * A project keeps a permanent record of every asset it used: one
 * `project_asset_assignments` row per use, carrying that use's quantity and
 * LCA facts (the six "per-use" fields, which moved off `assets`). Return
 * closes a row (history kept); remove deletes one added by mistake.
 *
 * Dates are business dates — plain calendar days, `YYYY-MM-DD` on the wire,
 * validated with the shared `calendarDateSchema` (docs/DATES.md).
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
/** How an assignment was closed — mirrors the `return_action` CHECK. */
export const RETURN_ACTIONS = ["returned", "stored", "transferred", "retired", "deleted"];
/**
 * The actions a caller may request on `POST …/return`. `transferred` is
 * written only by assign (moving an asset to another project) and `deleted`
 * only by the asset-delete trigger.
 */
export const REQUESTABLE_RETURN_ACTIONS = ["returned", "stored", "retired"];
/**
 * The six per-use fields, in column order. On `POST …/projects/:projectId`
 * (assign) an item that OMITS one of the five {@link ASSET_DEFAULT_FIELDS}
 * gets the thing's default (`assets.<field>`); an explicit `null` means "none
 * for this use". `quantity_used` has no default. On `PATCH` an omitted key is
 * left unchanged.
 */
export const ASSET_USE_FIELDS = [
    "quantity_used",
    "unit_code",
    "procurement_type_id",
    "end_of_life_plan_id",
    "waste_treatment_method_id",
    "sustainability_notes",
];
/**
 * The five per-use fields a thing also carries as its own DEFAULT
 * (`assets.<field>`), copied into each new project use when the assign item
 * omits the key — every per-use field except `quantity_used`.
 */
export const ASSET_DEFAULT_FIELDS = [
    "unit_code",
    "procurement_type_id",
    "end_of_life_plan_id",
    "waste_treatment_method_id",
    "sustainability_notes",
];
// ---------------------------------------------------------------------------
// Write bodies
// ---------------------------------------------------------------------------
const uuid = z.string().uuid();
/**
 * The six per-use fields, each optional and nullable (null clears it). Keep
 * `.optional()` (never `.default()`): an ABSENT key must reach the RPC absent,
 * because on assign absent = the thing's default and null = none.
 */
const assetUseShape = {
    quantity_used: z.number().finite().nullable().optional(),
    unit_code: z.string().nullable().optional(),
    procurement_type_id: uuid.nullable().optional(),
    end_of_life_plan_id: uuid.nullable().optional(),
    waste_treatment_method_id: uuid.nullable().optional(),
    sustainability_notes: z.string().nullable().optional(),
};
export const assignItemSchema = z.object({ asset_id: uuid, ...assetUseShape }).strict();
/**
 * `POST /orgs/:orgId/projects/:projectId` — assign one or more assets.
 * Duplicate `asset_id`s are deliberately NOT refused here: the RPC is the one
 * authority and answers with the coded `duplicate_item` envelope (naming the
 * asset), which the web turns into its specific toast. A Zod refusal would be
 * an uncoded 400.
 */
export const assignBodySchema = z
    .object({
    items: z.array(assignItemSchema).min(1),
    assigned_on: calendarDateSchema.optional(),
})
    .strict();
/** `PATCH /orgs/:orgId/:id` — the six per-use fields only; anything else is refused. */
export const patchBodySchema = z
    .object(assetUseShape)
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field must be provided",
});
/** `POST /orgs/:orgId/return` — close open assignments. */
export const returnBodySchema = z
    .object({
    ids: z.array(uuid).min(1),
    action: z.enum(REQUESTABLE_RETURN_ACTIONS),
    returned_on: calendarDateSchema.optional(),
})
    .strict();
//# sourceMappingURL=assetAssignments.js.map